import { api } from '@/api';
import { ActionContext } from 'vuex';
import { IAppointmentCreate, IAppointmentUpdate, IGoogleEventUpdate, ISplitProposalV2, IUserProfile, ITable, IMoveOwner} from '@/interfaces';
import { State } from '../state';
import { AppointmentState } from './state';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetAppointment, commitRemoveAppointment, commitSetAppointments, commitSetUnsyncedEvents, commitSetTable, commitSetSplitProposals, commitSetUnconfirmedAppointments} from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { translateError } from '../utils';
import { ISendEAttestInput } from '@/interfaces/eattest';
import { i18n } from '@/plugins/i18n';

type MainContext = ActionContext<AppointmentState, State>;

export const actions = {
  async actionGetAppointments(context: MainContext) {
    try {
      const response = await api.getAppointments(null, null, null);
      if (response) {
        commitSetAppointments(context, response.data);
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetAppointmentsWithStartAndEnd(context: MainContext, payload: {start: string, end: string}) {
    try {
      const response = await api.getAppointments(payload.start, payload.end, null);
      if (response) {
        commitSetAppointments(context, response.data);
        return response.data
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetAppointmentsForPatient(context: MainContext, payload: {patient_id: number}) {
    try {
      const response = await api.getAppointments(null, null, payload.patient_id);
      if (response) {
        commitSetAppointments(context, response.data);
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetAppointmentHonorarium(context: MainContext, payload: {id: number}) {
    try {
      const response = await api.getAppointmentHonorarium(payload.id);
      if (response) {
        return response.data;
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetTable(context: MainContext, payload: { start: string, end: string }) {
    try {
      const response = await api.getAppointmentTable(payload.start, payload.end);
      if (response) {
        commitSetTable(context, response.data);
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetSplitProposals(context: MainContext, payload: {table: ITable[], up: IUserProfile}) {
    async function rowToProposal(r) {
      console.log('row to split')
      console.log('table')
      const response = (await api.getSplitProposal(r.id)).data;
      const result: ISplitProposalV2 = {
        id: r.id,
        patient: r.patient,
        owner_email: r.owner_email,
        proposal1_start : response[0].start,
        proposal1_end : response[0].end,
        proposal2_start : response[1].start,
        proposal2_end : response[1].end
      }
      return result
    }

    const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };

    try {
      console.log('full table')
      console.log(payload.table)
      console.log(payload.up?.email)
      const splittableTableRowsTemp = (payload.table
        .filter(r => (r.duration == '60') && (r.owner_email == payload.up?.email) && (r.date_prescription)
        )
      )
      console.log(splittableTableRowsTemp)
      const results = await Promise.all(splittableTableRowsTemp.map(rowToProposal).map(p => p.catch(e => e)));
      const splittableTableRows = results.filter(result => !(result instanceof Error));
      commitSetSplitProposals(context, splittableTableRows);
    } catch (error: any) {
      if (error.response && error.response.status == 400 && error.response.data.detail == 'Appointment not found'){
        commitRemoveNotification(context, loadingNotification);
        commitAddNotification(context, { content: String(i18n.t('calendar.appointmentNotFound')), color: 'error' });
      }
      if (error.response && error.response.status == 400 && error.response.data.detail == 'Not enough permissions'){
        commitRemoveNotification(context, loadingNotification);
        commitAddNotification(context, { content: String(i18n.t('calendar.noAccessToAppointment')), color: 'error' });
      }
      if (error.response && error.response.status == 400 && error.response.data.detail == 'Only 60m appointments can be split'){
        commitRemoveNotification(context, loadingNotification);
        commitAddNotification(context, { content: String(i18n.t('calendar.only60MinCanBeSplit')), color: 'error' });
      }
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetUnsyncedEvents(context: MainContext, payload: {performSync: boolean, start_date: string | null, end_date: string | null}) {
    try {
      if (payload.performSync){
        const pendingNotification = { content: String(i18n.t('calendar.syncingWithGoogle')), color: 'orange' }
        commitAddNotification(context, pendingNotification);
        const response = await api.syncEvents(payload.performSync, payload.start_date, payload.end_date);
        commitRemoveNotification(context, pendingNotification);
        if (response) {
          commitSetUnsyncedEvents(context, response.data);
        }
      } else {
        const response = await api.syncEvents(payload.performSync, payload.start_date, payload.end_date);
        if (response) {
          commitSetUnsyncedEvents(context, response.data);
        }
      }


    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateEvent(context: MainContext, payload: { id: number, event: IGoogleEventUpdate }) {
    const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };
    try {
      commitAddNotification(context, loadingNotification);
      await Promise.all([
        api.updateEvent(payload.id, payload.event),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('calendar.editingAppointment')), color: 'success' });
    } catch (error: any) {
      await commitRemoveNotification(context, loadingNotification);
      if (error.response && error.response){
        console.log('hi')
        const translated = translateError(error.response.data.detail)
        console.log('hey')
        console.log(translated)
        commitAddNotification(context, { content: translated, color: 'error' });
      }
      else {
        await dispatchCheckApiError(context, error);
      }
    }
  },
  async actionUpdateAppointment(context: MainContext, payload: { id: number, appointment: IAppointmentUpdate }) {
    const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };
    if (payload.appointment.start == 'Invalid DateTime' || payload.appointment.end == 'Invalid DateTime') {
      commitAddNotification(context, { content: String(i18n.t('calendar.updateFailedInvalidDate')), color: 'error' });
      return;
    }
    try {
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.updateAppointment(payload.id, payload.appointment),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetAppointment(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('calendar.appointmentUpdated')), color: 'success' });
      return response.data;
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionDeleteAppointment(context: MainContext, payload: { id: number; forceDelete?: boolean }) {
    const loadingNotification = { content: String(i18n.t('common.delete')), showProgress: true };
    try {
      commitAddNotification(context, loadingNotification);
      await Promise.all([
        api.deleteAppointment(payload.id, payload.forceDelete),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitRemoveAppointment(context, payload.id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('calendar.appointmentDeleted')), color: 'success' });
    } catch (error: any) {
      commitRemoveNotification(context, loadingNotification);
      if (error.response && error.response.status == 400 && error.response.data.detail == 'Cannot delete appointment with efact_id or attestation_id') {
        commitAddNotification(context, { content: String(i18n.t('calendar.deleteDisabledAttestationOrEFact')), color: 'error' });
      }
      else if (error.response && error.response.status == 400) {
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedGeneric', { detail: error.response.data.detail })), color: 'error' });
      }
      else {
        await dispatchCheckApiError(context, error);
      }
    }
  },
  async actionCreateAppointment(context: MainContext, payload: IAppointmentCreate) {
    const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };
    if (payload.start == 'Invalid DateTime' || payload.end == 'Invalid DateTime'){
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('calendar.createFailedInvalidDate')), color: 'error' });
      return;
    }
    try {
      commitAddNotification(context, loadingNotification);
      const response = await api.createAppointment(payload);
      commitSetAppointment(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('calendar.appointmentCreated')), color: 'success' });
    } catch (error: any) {
      commitRemoveNotification(context, loadingNotification);
      if (error.response && error.response && error.response.status == 400 && error.response.data.detail == 'Prescription used up'){
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedPrescriptionUsedUp')), color: 'error' });
      }
      else if (error.response && error.response.status == 400 && error.response.data.detail == 'First appointment cannot be more than 2 months after prescription date'){
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedTooEarlyAfterPrescription')), color: 'error' });
      }
      else if (error.response && error.response.status == 400 && error.response.data.detail == 'Appointment cannot be more than 2 months after last prescription appointment'){
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedTooLateAfterLastAppointment')), color: 'error' });
      }
      else if (error.response && error.response.status == 400 && error.response.data.detail == 'An appointment for this patient at this time already exists'){
        commitAddNotification(context, { content: String(i18n.t('calendar.duplicateAppointment')), color: 'error' });
      }
      else if (error.response && error.response.status == 400 && error.response.data.detail == 'Appointment cannot start before prescription date'){
        commitAddNotification(context, { content: String(i18n.t('calendar.appointmentDateBeforePrescription')), color: 'error' });
      }
      else if (error.response) {
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedGeneric', { detail: error.response.data.detail })), color: 'error' });

      }
      else {
        await dispatchCheckApiError(context, error);
      }
    }
  },
  async actionChangeOwner(context: MainContext, payload: { id: number, targetOwnerid: number }) {
    const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };
    try {
      commitAddNotification(context, loadingNotification);
      const data: IMoveOwner = {
        target_owner_id: payload.targetOwnerid
      }
      const response = await api.moveAppointment(payload.id, data);
      commitSetAppointment(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('calendar.appointmentMoved')), color: 'success' });
    } catch (error: any) {
      commitRemoveNotification(context, loadingNotification);
      if (error.response) {
        commitAddNotification(context, { content: String(i18n.t('calendar.moveFailedGeneric', { detail: error.response.data.detail })), color: 'error' });

      }
      else {
        await dispatchCheckApiError(context, error);
      }
    }
  },
  async actionSubmitProposal(context: MainContext, payload) {
    try {
      await api.submitSplitProposal(payload.id, payload.data);
    } catch (error: any) {
      if (error.response && error.response.status == 400 && error.response.data.detail == 'Prescription used up'){
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedPrescriptionUsedUp')), color: 'error' });
      }
      if (error.response && error.response.status == 400 && error.response.data.detail == 'First appointment cannot be more than 2 months after prescription date'){
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedTooEarlyAfterPrescription')), color: 'error' });
      }
      if (error.response && error.response.status == 400 && error.response.data.detail == 'Appointment cannot be more than 2 months after last prescription appointment'){
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedTooLateAfterLastAppointment')), color: 'error' });
      }
      if (error.response && error.response.status == 400 && error.response.data.detail.constructor == Object && 'message' in error.response.data.detail && error.response.data.detail.message == 'Prescription used up'){
        const patient = error.response.data.detail.patient
        commitAddNotification(context, { content: String(i18n.t('calendar.createFailedPrescriptionUsedUpForPatient', { firstName: patient.first_name, lastName: patient.last_name })), color: 'error', url: `/main/prescriptions/create?patient_id=${patient.id}` });
      }
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetUnconfirmedAppointments(context: MainContext) {
    try {
      const response = await api.getUnconfirmedAppointments();
      if (response) {
        commitSetUnconfirmedAppointments(context, response.data);
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionConfirmUnconfirmedAppointment(context: MainContext, payload: { appointment_id: number, patient_id: number}) {
    try {
      const response = await api.confirmUnconfirmedAppointment(payload.appointment_id, payload.patient_id);
      if (response) {
        console.log('confirmed')
        console.log(response.data)
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionDeleteUnconfirmedAppointment(context: MainContext, id: number) {
    try {
      const response = await api.deleteUnconfirmedAppointment(id);
      if (response) {
        console.log('deleted')
        console.log(response.data)
      }
    } catch (error: any) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionHandlePaymentDialog(context: MainContext, payload) {
    try {
      await api.handlePaymentDialog(payload);
    } catch (error: any) {
      commitAddNotification(context, { content: error.response.data.detail, color: 'error' });
    }
  },
  async actionCheckConflictUnconfirmed(context: MainContext, payload: {appointment_id: number}) {
    try {
      const response = (await Promise.all([
        api.checkConflictUnconfirmed(payload.appointment_id),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      return response.data
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false
    }
  },
  async actionSendAttestation(context: MainContext, payload: {input: ISendEAttestInput[], force_retryable: boolean}) {
    try {
      const response = (await Promise.all([
        api.sendEAttest(payload.input, payload.force_retryable),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];

      commitAddNotification(context, { content: String(i18n.t('attestations.eAttestApproved')), color: 'success' });
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response){
        const detail = error.response.data?.detail;
        let error_message = typeof detail === 'string' ? detail : (detail?.message ?? '');
        if (error.response.status == 401){
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = typeof detail === 'object' ? detail?.detail : undefined;
        if (!error_message) {
          error_message = String(i18n.t('attestations.eAttestFailedGeneric'));
        }
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      } else {
        await dispatchCheckApiError(context, error);
        commitAddNotification(context, { color: 'error', content: String(i18n.t('attestations.eAttestFailedGeneric')) });
      }
    }
  },
  async actionCancelEAttest(context: MainContext, payload: {
    id: number,
    forceRetryable: boolean
  }) {
    const loadingNotification = { content: String(i18n.t('common.cancel')), showProgress: true };
    commitAddNotification(context, loadingNotification);
    try {
      await Promise.all([
        api.cancelEttest(payload.id, payload.forceRetryable),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('attestations.eAttestCancelled')), color: 'success' });
    } catch (error: any) {
      commitRemoveNotification(context, loadingNotification);
      if (error.response){
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401){
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = error.response.data.detail.detail;
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },
  async actionEmailAttest(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: String(i18n.t('common.sendingEmail')), showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.emailAttest(id),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('invoices.invoiceSent')), color: 'success' });
      return response.data
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
};

const { dispatch } = getStoreAccessors<AppointmentState, State>('');

export const dispatchCreateAppointment = dispatch(actions.actionCreateAppointment);
export const dispatchChangeOwner = dispatch(actions.actionChangeOwner);
export const dispatchGetAppointments = dispatch(actions.actionGetAppointments);
export const dispatchGetAppointmentsWithStartAndEnd = dispatch(actions.actionGetAppointmentsWithStartAndEnd);
export const dispatchGetAppointmentsForPatient = dispatch(actions.actionGetAppointmentsForPatient);
export const dispatchGetUnconfirmedAppointments = dispatch(actions.actionGetUnconfirmedAppointments);
export const dispatchGetTable = dispatch(actions.actionGetTable);
export const dispatchGetSplitProposals = dispatch(actions.actionGetSplitProposals);
export const dispatchGetUnsyncedEvents = dispatch(actions.actionGetUnsyncedEvents);
export const dispatchUpdateAppointment = dispatch(actions.actionUpdateAppointment);
export const dispatchDeleteAppointment = dispatch(actions.actionDeleteAppointment);
export const dispatchUpdateGoogleEvent = dispatch(actions.actionUpdateEvent);
export const dispatchSubmitProposal = dispatch(actions.actionSubmitProposal);
export const dispatchConfirmUnconfirmedAppointment = dispatch(actions.actionConfirmUnconfirmedAppointment);
export const dispatchDeleteUnconfirmedAppointment = dispatch(actions.actionDeleteUnconfirmedAppointment);
export const dispatchHandlePaymentDialog = dispatch(actions.actionHandlePaymentDialog);
export const dispatchCheckConflictUnconfirmed = dispatch(actions.actionCheckConflictUnconfirmed);
export const dispatchGetAppointmentHonorarium = dispatch(actions.actionGetAppointmentHonorarium);
export const dispatchSendAttestation = dispatch(actions.actionSendAttestation);
export const dispatchCancelEAttest = dispatch(actions.actionCancelEAttest);
export const dispatchEmailAttest = dispatch(actions.actionEmailAttest)